import { Typography } from '@mui/material';
import { AnalyticsEventName, trackAmplitudeEvent } from '@schooly/analytics';
import {
  changeAttendanceCodesOrder,
  createAttendanceCode,
  editAttendanceCode,
  removeAttendanceCode,
  useGetAttendanceCodesQuery,
} from '@schooly/api';
import { AttendanceCode } from '@schooly/api';
import { ApiError } from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import { useConfirmationDialog } from '@schooly/components/confirmation-dialog';
import { useInvalidateListQueriesFor } from '@schooly/components/filters';
import { useNotifications } from '@schooly/components/notifications';
import {
  AttendanceCodeAction,
  AttendanceCodeInSchool,
  AttendanceCodeIsPresent,
} from '@schooly/constants';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { useRouter } from '../context/router/useRouter';
import useRequestWithProgress from './useRequestWithProgress';
import useUserCounts from './useUserCounts';

export type AttendanceCodeForm = Omit<
  AttendanceCode,
  'in_school' | 'is_present' | 'has_push_notifications' | 'has_justification' | 'order' | 'id'
> & {
  id?: string;
  order?: number;
  in_school: AttendanceCodeInSchool;
  is_present: AttendanceCodeIsPresent;
  action: AttendanceCodeAction;
};

export const useAttendanceCodes = () => {
  const { id: codeId } = useParams();
  // const dispatch = useAppDispatch();
  const { goBack } = useRouter();
  const invalidateAttendanceQueries = useInvalidateListQueriesFor('attendance');

  const { schoolId = '' } = useAuth();
  const { data: attendanceCodes, isLoading } = useGetAttendanceCodesQuery(schoolId, {
    refetchOnMount: 'always',
  });

  const { getConfirmation } = useConfirmationDialog();

  const { showError, showNotification } = useNotifications();
  const { formatMessage } = useIntl();
  const { userCounts } = useUserCounts();

  const isEditing = !!codeId;

  const goToSettingsPage = useCallback(() => {
    goBack();
  }, [goBack]);

  const renderJustificationConfirmation = useCallback(
    (code: { name: string; hasJustification: boolean }) => {
      return (
        <>
          <Typography variant="h1">
            {formatMessage(
              {
                id: code.hasJustification
                  ? 'attendance-justification-EnableConfirmation'
                  : 'attendance-justification-DisableConfirmation',
              },
              { codeName: `"${code.name}"` },
            )}
          </Typography>
          <Typography mt={1.5} variant="h3">
            {formatMessage(
              {
                id: code.hasJustification
                  ? 'attendance-justification-EnableConfirmation-Description'
                  : 'attendance-justification-DisableConfirmation-Description',
              },
              { codeName: `"${code.name}"` },
            )}
          </Typography>
        </>
      );
    },
    [formatMessage],
  );

  const saveCode = useCallback(
    async (currentCode: AttendanceCodeForm) => {
      if (!schoolId) {
        return;
      }

      const successNotification = () =>
        showNotification({
          textId: codeId ? 'confirmation-SuccessfulEdited' : 'confirmation-SuccessfulSaved',
          values: { name: currentCode.name },
          type: 'success',
        });

      const { id, action, ...code } = currentCode;

      const normalizedFormData: Omit<AttendanceCode, 'id'> = {
        ...code,
        in_school: !!code.in_school,
        is_present: !!code.is_present,
        has_push_notifications: action === AttendanceCodeAction.NotifyParents,
        has_justification: action === AttendanceCodeAction.RequestJustification,
        order: code.order ?? (attendanceCodes?.length ?? 0) + 1,
      };

      const existingCode = attendanceCodes?.find((c) => c.id === codeId);
      const hasJustificationChanged =
        !!existingCode && existingCode.has_justification !== normalizedFormData.has_justification;
      const hasAttendanceRecords = !!userCounts?.attendance_record;
      const showJustificationConfirmation = hasJustificationChanged && hasAttendanceRecords;

      if (!codeId) {
        try {
          await createAttendanceCode(schoolId, [normalizedFormData]);
          invalidateAttendanceQueries();

          successNotification();
          goToSettingsPage();
        } catch (err) {
          console.error(err);
          showError(err as ApiError);
        }
      } else {
        try {
          if (showJustificationConfirmation) {
            const confirmed = await getConfirmation({
              confirmTextId: 'action-Continue',
              content: renderJustificationConfirmation({
                name: normalizedFormData.name,
                hasJustification: normalizedFormData.has_justification,
              }),
            });

            if (!confirmed) {
              return;
            }
          }

          await editAttendanceCode({ ...normalizedFormData, id: codeId });
          invalidateAttendanceQueries();

          successNotification();
          goToSettingsPage();

          trackAmplitudeEvent({
            name: AnalyticsEventName.AttendanceCodeSave,
          });
        } catch (err) {
          console.error(err);
          showError(err as ApiError);
        }
      }
    },
    [
      schoolId,
      attendanceCodes,
      userCounts?.attendance_record,
      codeId,
      showNotification,
      invalidateAttendanceQueries,
      goToSettingsPage,
      showError,
      getConfirmation,
      renderJustificationConfirmation,
    ],
  );

  const changeCodeOrder = useCallback(
    async (codes: Pick<AttendanceCode, 'id' | 'order'>[]) => {
      if (!schoolId) {
        return;
      }

      try {
        await changeAttendanceCodesOrder({ schoolId, order: codes });
        invalidateAttendanceQueries();

        goToSettingsPage();
      } catch (err) {
        console.error(err);
        showError(err as ApiError);
      }
    },
    [goToSettingsPage, schoolId, showError, invalidateAttendanceQueries],
  );

  const deleteCode = useCallback(
    async (code: AttendanceCodeForm) => {
      if (!schoolId || !codeId) {
        return;
      }

      try {
        const confirmed = await getConfirmation({
          cancelTextId: 'no',
          confirmTextId: 'yes',
          message: formatMessage(
            { id: 'attendance-CodeRemoveConfirmAction' },
            { codeName: code.name },
          ),
        });

        if (!confirmed) {
          return;
        }

        // TR-3744 FE: Do not allow deleting attendance codes for now
        const res = await removeAttendanceCode({ codeId, confirmed: false });

        if (res?.code_to_delete) {
          showError({
            reason: formatMessage({ id: 'attendance-CodeRemoveError' }),
          });
          return;
        }

        invalidateAttendanceQueries();
        goToSettingsPage();
      } catch (err) {
        console.error(err);
        showError(err as ApiError);
      }
    },
    [
      formatMessage,
      codeId,
      getConfirmation,
      goToSettingsPage,
      schoolId,
      showError,
      invalidateAttendanceQueries,
    ],
  );

  const [deleteAttendanceCode, isDeletingInProgress] = useRequestWithProgress(deleteCode);
  const [changeAttendanceCodeOrder, isChangeOrderInProcess] =
    useRequestWithProgress(changeCodeOrder);
  const [saveAttendanceCode, isSavingInProcess] = useRequestWithProgress(saveCode);

  return {
    isEditing,
    isLoading: isLoading || isChangeOrderInProcess || isSavingInProcess,
    isDeleting: isDeletingInProgress,
    codeId,
    attendanceCodes: attendanceCodes ?? [],
    onClose: goToSettingsPage,
    saveAttendanceCode,
    changeAttendanceCodeOrder,
    deleteAttendanceCode,
  };
};
